<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: {
    type: Number,
    default: null,
  },
});

const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);
const enabled = computed(() =>
  Boolean(props.conversationId && accountId.value)
);

const currentChat = useMapGetter('getSelectedChat');
const getInboxById = useMapGetter('inboxes/getInbox');

// Company of the conversation, by inbox-name suffix — same convention as
// StorePanelContainer/ChatList (-MK / -S3, owner rule 2026-07-01). Routing is
// authoritative server-side (MhAiAssistController branches by inbox id); this
// only drives the label + which cancel path the browser calls. On S3 the
// button reads "AI Handover": the Smith3D AI box takes over the customer.
const isS3 = computed(() => {
  const inbox = getInboxById.value(currentChat.value?.inbox_id);
  return /[-\s]S3$/i.test((inbox?.name || '').trim());
});

const assistLabel = computed(() => (isS3.value ? 'AI Handover' : 'Assist Now'));

// Server-reported state.
const phase = ref('idle');
const statusTs = ref(0); // wall-clock ts of the latest n8n status emit
const fireBusy = ref(false);

// Run-elapsed + terminal-surfacing state (live-progress UX, 2026-07-13). The agentic run can take
// minutes; without these the badge froze on one label then vanished when the flow:status TTL expired,
// and a FAILED turn looked identical to idle ("I pressed it and nothing happened"). runStartTs is set
// when a run first goes non-terminal and cleared on any terminal (drives the live elapsed readout);
// errorSince timestamps a terminal 'error' so the failure badge shows briefly + retryably.
const runStartTs = ref(0);
const errorSince = ref(0);
const SLOW_HINT_MS = 90000; // past this the badge says "still working" (busy box, not broken)
const ERROR_SHOW_MS = 30000; // how long the "couldn't finish — retry" badge stays before reverting

// Local UI intent latch. The poll mirrors n8n's flow:status, which keeps
// advancing — and being rewritten — even AFTER the agent clicks Cancel, and can
// briefly read a terminal phase right after Assist Now before n8n registers the
// flow. Without a latch the button morphs Assist Now <-> Cancel on every such
// poll ("flashing"), and Cancel looks broken because the badge snaps straight
// back. The latch holds the agent's intended state until the server agrees.
const intent = ref(null); // 'assist' | 'cancel' | null
const intentTs = ref(0);
// Cancel holds 'idle' until the cancelled run actually drains (so the badge
// doesn't reappear mid-run); assist holds 'queued' only until n8n registers a
// live phase. Both have a safety cap so a stuck flow can't freeze the control.
const CANCEL_HOLD_MS = 30000;
const ASSIST_HOLD_MS = 8000;
const TERMINAL_PHASES = ['idle', 'done', 'escalated', 'error'];
const isTerminal = p => !p || TERMINAL_PHASES.includes(p);
// A cancelled run is only "over" on a real end-of-run phase — NOT on 'idle'. The
// cancel endpoint itself resets flow:status to 'idle', and n8n then writes one
// more phase (drafting/sending/done) as it finishes; treating 'idle' as the end
// would release the latch too early and let that trailing write flash the badge
// back. So hold the cancel latch until done/escalated/error (or the time cap).
const RUN_ENDED_PHASES = ['done', 'escalated', 'error'];
// Phases where the AI flow is actively running (drives the status badge + the
// faster poll cadence). Single source of truth, reused by isProcessing below.
const PROCESSING_PHASES = [
  'queued',
  'recall',
  'triaging',
  'reviewing',
  'drafting',
  'sending',
];

// Client clock — drives the live debounce countdown without polling.
const nowTs = ref(Date.now());

// Debounce wait is fixed at 15s in the n8n "Debounce Wait" node (workflow
// rAEMt5eehN0Cno7t, amount:15) — drives the live countdown.
const DEBOUNCE_MS = 15000;

let pollTimer = null;
let nowTicker = null;

const baseUrl = () =>
  `/api/v1/accounts/${accountId.value}/mh_ai_assist/${props.conversationId}`;

// Reconcile a freshly-polled server phase against any pending local intent so
// the control stays stable instead of flickering while n8n's status churns.
const applyServerStatus = (serverPhase, serverTs) => {
  if (intent.value === 'cancel') {
    const expired = Date.now() - intentTs.value > CANCEL_HOLD_MS;
    // Release the latch when the cancelled run truly ends (done/escalated/error),
    // when a brand-NEW flow begins (a 'debouncing' with a newer ts than the
    // cancel — a fresh inbound worth showing; NOT the same debounce we just
    // cancelled), or on the safety cap. Otherwise hold 'idle': ignore the
    // trailing processing writes and the endpoint's own 'idle' reset so the
    // badge can't flash back.
    const freshRun = serverPhase === 'debouncing' && serverTs > intentTs.value;
    if (RUN_ENDED_PHASES.includes(serverPhase) || freshRun || expired) {
      intent.value = null;
      phase.value = serverPhase;
      statusTs.value = serverTs;
    }
    return;
  }

  if (intent.value === 'assist') {
    const expired = Date.now() - intentTs.value > ASSIST_HOLD_MS;
    // Hold the optimistic 'queued' until n8n registers a live (non-terminal)
    // phase, so we don't blink back to Assist Now before the flow shows up.
    if (!isTerminal(serverPhase) || expired) {
      intent.value = null;
      phase.value = serverPhase;
      statusTs.value = serverTs;
    }
    return;
  }

  phase.value = serverPhase;
  statusTs.value = serverTs;
};

const fetchStatus = async () => {
  if (!enabled.value) return;
  let serverPhase = 'idle';
  let serverTs = 0;
  try {
    const res = await window.axios.get(`${baseUrl()}/status`);
    const data = res.data || {};
    serverPhase = data.phase || 'idle';
    serverTs = data.statusTs || 0;
  } catch (_err) {
    // Silent — keep last-known state. Don't noisily fail in CS UI just
    // because mk-backend hiccupped.
    return;
  }
  applyServerStatus(serverPhase, serverTs);
};

// Live countdown derived from statusTs while phase=debouncing. Updates every
// 100ms via nowTicker so the digit transitions feel smooth between polls.
const remainingMs = computed(() => {
  if (phase.value !== 'debouncing' || !statusTs.value) return 0;
  return Math.max(0, DEBOUNCE_MS - (nowTs.value - statusTs.value));
});

// Poll fast only while something is actually happening. Back off hard when
// idle/terminal so a conversation left open (often a background tab open all
// day) stops hitting mk-backend every 1.5s. Idle polling only needs to notice
// an externally triggered flow starting; up to ~5s latency there is fine since
// the debounce is 15s and its countdown is interpolated client-side.
const pollInterval = computed(() => {
  if (phase.value === 'debouncing' || phase.value === 'queued') return 500;
  if (PROCESSING_PHASES.includes(phase.value)) return 1500;
  return 5000;
});

const stopPolling = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};

const schedulePoll = () => {
  stopPolling();
  pollTimer = setTimeout(async () => {
    // Skip the network call while the tab is backgrounded — a hidden tab needs
    // no live status and shouldn't keep hitting mk-backend. The timer keeps
    // cycling; onVisible does an immediate refresh when the agent returns.
    if (!document.hidden) await fetchStatus();
    schedulePoll();
  }, pollInterval.value);
};

// S3 handover pre-flight: a small optional "brief the AI" popup. The note (if
// any) rides the assist POST as { note } and reaches the Smith3D box as
// staff_note. MK Assist Now fires immediately, exactly as before — the popup
// exists ONLY on S3 conversations.
const showHandoverPopup = ref(false);
const handoverNote = ref('');
// Kept in script so vue/no-bare-strings-in-template stays quiet — this fork
// component is intentionally English-only, matching its other labels.
const HANDOVER_TITLE = 'Hand over to Smith3D AI (1–5 mins)';

// Preset briefs staff can toggle instead of typing under load. The chip label
// stays short; the note is the full instruction the S3 AI receives. Selected
// notes + any free text compose into one staff_note (newline-joined).
// Chip set shaped by the S3 AI's own staff-turn analysis (2026-09-04): plain
// takeover is the pre-selected default (84% of handovers carry no staff-only
// signal); quote/warranty field lists follow what staff actually ask, in one
// consolidated message; "Reject kindly" deferred to phase 2 (needs a typed
// reason + fights the financial-terms gate).
const HANDOVER_PRESETS = [
  {
    key: 'takeover',
    label: 'Take over — I’m busy',
    note: 'Take over this conversation and handle it end-to-end — staff is busy.',
  },
  {
    key: 'quote',
    label: 'Gather quote details',
    note: 'Ask for the missing quote details in ONE consolidated message (never a drip of questions): material, printer model, photo of the part, delivery address, name/company/PO, colour, deadline, use case, budget. Skip anything the chat already answered.',
  },
  {
    key: 'warranty',
    label: 'Warranty intake',
    note: 'Collect the warranty claim in one message using the canon form — phone, model, serial number, purchase date, problem — plus photo of the fault, proof of purchase, and where it was bought. Never state whether warranty covers it.',
  },
  {
    key: 'stock',
    label: 'Check stock',
    note: 'Check stock for the item(s) asked. Pull a fresh product lookup first to confirm the EXACT product; if the exact item can’t be confirmed, use the soft fallback (team will confirm) instead of guessing.',
  },
  {
    key: 'apology',
    label: 'Sorry for the mistake',
    note: 'Apologise for our mistake, reassure the customer, and sort out the fix.',
  },
  {
    key: 'draft',
    label: 'Draft only — don’t send',
    note: 'Prepare the reply as an internal draft note only — do NOT send anything to the customer; staff will review it first.',
  },
];
const DEFAULT_PRESET = 'takeover';
const TASK_KEYS = ['quote', 'warranty', 'stock', 'apology'];
const selectedPresets = ref([]);

// 'Take over' and the specific task chips are mutually exclusive (it IS the
// no-specific-task case); 'draft only' is a mode and rides along with either.
const togglePreset = key => {
  const current = selectedPresets.value;
  if (current.includes(key)) {
    selectedPresets.value = current.filter(k => k !== key);
    return;
  }
  let next = [...current, key];
  if (key === DEFAULT_PRESET) next = next.filter(k => !TASK_KEYS.includes(k));
  else if (TASK_KEYS.includes(key))
    next = next.filter(k => k !== DEFAULT_PRESET);
  selectedPresets.value = next;
};

const closeHandoverPopup = () => {
  showHandoverPopup.value = false;
};

const fireAssist = async () => {
  if (!enabled.value || fireBusy.value) return;
  fireBusy.value = true;
  const parts = HANDOVER_PRESETS.filter(preset =>
    selectedPresets.value.includes(preset.key)
  ).map(preset => preset.note);
  const extra = handoverNote.value.trim();
  if (extra) parts.push(extra);
  const note = parts.join('\n');
  showHandoverPopup.value = false;
  // Optimistic + latched: show 'queued' (badge + Cancel) immediately and hold it
  // so the next polls don't bounce us back to Assist Now before n8n registers
  // the flow. The latch clears as soon as the server reports a live phase.
  intent.value = 'assist';
  intentTs.value = Date.now();
  phase.value = 'queued';
  statusTs.value = Date.now();
  try {
    await window.axios.post(
      `${baseUrl()}/assist_now`,
      note ? { note } : undefined
    );
    handoverNote.value = '';
    selectedPresets.value = [];
  } catch (_err) {
    // Firing failed — drop the latch so the UI reverts on the next poll.
    // The note is kept so staff can retry without retyping.
    intent.value = null;
    phase.value = 'idle';
  } finally {
    fireBusy.value = false;
  }
};

const requestAssist = () => {
  if (!enabled.value || fireBusy.value) return;
  if (isS3.value) {
    // Fresh open pre-selects plain takeover so the common case is one click.
    if (!showHandoverPopup.value) selectedPresets.value = [DEFAULT_PRESET];
    showHandoverPopup.value = !showHandoverPopup.value;
    return;
  }
  fireAssist();
};

// Cancel — only offered while the AI is actively running (isProcessing). Sets a
// server-side suppress flag (n8n's Send Gate halts this turn's output) and
// clears the phase so the badge + button revert to Assist Now at once.
const cancelAi = async () => {
  if (!enabled.value) return;
  // Latch 'cancel' so the badge + Cancel button don't snap back when n8n writes
  // the next flow:status before the run drains. UI returns to Assist Now at once
  // and stays there; the server-side suppress flag is the authoritative stop.
  intent.value = 'cancel';
  intentTs.value = Date.now();
  phase.value = 'idle';
  try {
    if (isS3.value) {
      // S3 cancels through the Rails proxy so the Smith3D AI box URL/secret
      // stay server-side. MK keeps its proven direct sapi call below.
      await window.axios.post(`${baseUrl()}/cancel`);
    } else {
      await fetch('https://sapi.mkhb.my/api/chat-triage/cancel-ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversationId: props.conversationId }),
      });
    }
  } catch (_err) {
    // best-effort; the server-side suppress flag is the authoritative stop
  }
};

watch(
  () => props.conversationId,
  () => {
    intent.value = null; // a different conversation = a clean slate
    phase.value = 'idle';
    statusTs.value = 0;
    if (enabled.value) {
      fetchStatus();
      schedulePoll();
    }
  }
);

// Track run elapsed + error surfacing off the raw phase. Entering any non-terminal phase starts the
// clock (if not already running); any terminal stops it — and 'error' timestamps the failure badge so
// it shows for a bounded window instead of the control silently snapping back to Assist Now.
watch(phase, newPhase => {
  if (isTerminal(newPhase)) {
    runStartTs.value = 0;
    errorSince.value = newPhase === 'error' ? Date.now() : 0;
  } else {
    if (!runStartTs.value) runStartTs.value = Date.now();
    errorSince.value = 0;
  }
});

// Resume immediately when the tab is refocused so the agent sees fresh status
// without waiting out an interval (polling pauses while hidden — see schedulePoll).
const onVisible = () => {
  if (!document.hidden && enabled.value) {
    fetchStatus();
    schedulePoll();
  }
};

onMounted(() => {
  if (enabled.value) {
    fetchStatus();
    schedulePoll();
    nowTicker = setInterval(() => {
      nowTs.value = Date.now();
    }, 100);
  }
  document.addEventListener('visibilitychange', onVisible);
});

onBeforeUnmount(() => {
  stopPolling();
  if (nowTicker) clearInterval(nowTicker);
  document.removeEventListener('visibilitychange', onVisible);
});

// ─── Display logic ──────────────────────────────────────────────────────
// Per-phase labels for the morphing status badge. The badge only shows during
// active processing — once done/escalated/error/idle, the badge hides and only
// the Assist Now button remains.
const PHASE_LABELS = {
  queued: 'Starting…',
  recall: 'Recalling history…',
  triaging: 'AI thinking…',
  reviewing: 'Manager reviewing…',
  drafting: 'Drafting…',
  sending: 'Sending…',
};

const isProcessing = computed(() => PROCESSING_PHASES.includes(phase.value));

// Countdown mode: showing the debounce timer in the SAME badge slot.
const showCountdown = computed(
  () => phase.value === 'debouncing' && remainingMs.value > 0
);

// Processing mode: showing a per-phase label in the same badge slot.
const showProcessing = computed(
  () => !showCountdown.value && isProcessing.value
);

// Elapsed since the run started (client-tracked: the heartbeat keeps statusTs moving, so run-age can't
// be derived from it). Drives the live "(1m 05s)" readout so a long run reads as progressing, not frozen.
const elapsedMs = computed(() =>
  runStartTs.value ? Math.max(0, nowTs.value - runStartTs.value) : 0
);
const elapsedLabel = computed(() => {
  const s = Math.floor(elapsedMs.value / 1000);
  return s >= 60
    ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`
    : `${s}s`;
});
// Past the soft threshold the run is slow (busy box), not broken — say so instead of a frozen label.
const isSlow = computed(
  () => showProcessing.value && elapsedMs.value > SLOW_HINT_MS
);
// A FAILED turn (mk-agent emitted terminal 'error') — surface it briefly + retryably instead of
// silently reverting to the plain Assist Now button (which read as "nothing happened").
const showError = computed(
  () =>
    phase.value === 'error' &&
    errorSince.value > 0 &&
    nowTs.value - errorSince.value < ERROR_SHOW_MS
);

// Badge as a whole hides on done/escalated/idle; a recent 'error' keeps it visible (retry hint).
const showBadge = computed(
  () => showCountdown.value || showProcessing.value || showError.value
);

const countdownLabel = computed(
  () => `${Math.ceil(remainingMs.value / 1000)}s`
);
const processingLabel = computed(() => {
  const base = isSlow.value
    ? 'Still working'
    : PHASE_LABELS[phase.value] || 'AI working…';
  return elapsedMs.value > 2000 ? `${base} (${elapsedLabel.value})` : base;
});

// Single source of truth for the morphing badge across its three modes (countdown / processing / error).
const badgeText = computed(() => {
  if (showCountdown.value) return countdownLabel.value;
  if (showError.value) return 'AI couldn’t finish — retry';
  return processingLabel.value;
});
const badgeClass = computed(() => {
  if (showCountdown.value)
    return 'font-mono text-n-slate-12 bg-n-slate-3 border border-n-slate-5';
  if (showError.value) return 'text-n-ruby-9 bg-n-ruby-3';
  if (isSlow.value) return 'text-n-amber-11 bg-n-amber-3';
  return 'text-n-violet-9 bg-n-violet-3';
});
const badgeTitle = computed(() => {
  if (showCountdown.value)
    return `AI debounce: ${countdownLabel.value} until flow runs`;
  if (showError.value)
    return `The AI could not finish this turn (system busy or an error). Click ${assistLabel.value} to retry.`;
  if (isSlow.value)
    return `AI still working (${elapsedLabel.value}) — the system is busier than usual`;
  return `AI: ${processingLabel.value}`;
});

const assistTooltip = computed(() =>
  isS3.value
    ? 'Hand this customer over to the Smith3D AI — it takes over the conversation from here'
    : 'Run AI on the latest customer message'
);

// Cancel is offered the WHOLE time the AI is engaged — including the debounce
// countdown, where cancelling aborts the run before any LLM tokens are spent
// (n8n checks the flag right after the wait). Outside an active flow the button
// reverts to Assist Now.
const isCancellable = computed(
  () => isProcessing.value || phase.value === 'debouncing'
);
const cancelTooltip = computed(() =>
  showCountdown.value
    ? 'Cancel — stops the run before the AI starts (no tokens used)'
    : "Stop the AI — it won't post a draft or reply for this message"
);
</script>

<template>
  <div v-if="enabled" class="relative flex items-center gap-1.5">
    <!-- S3-only pre-handover popup: optional staff brief forwarded to the
         Smith3D AI as staff_note. Anchored above the toolbar control. -->
    <div
      v-if="showHandoverPopup"
      v-on-click-outside="closeHandoverPopup"
      class="absolute bottom-full right-0 z-50 mb-2 flex w-[26rem] flex-col gap-2.5 rounded-xl border border-n-weak bg-n-solid-2 p-3.5 shadow-xl"
      @keydown.esc="closeHandoverPopup"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ HANDOVER_TITLE }}
      </span>
      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="preset in HANDOVER_PRESETS"
          :key="preset.key"
          type="button"
          class="h-7 rounded-lg border px-2 text-xs font-medium transition-colors"
          :class="
            selectedPresets.includes(preset.key)
              ? 'border-n-brand bg-n-brand/10 text-n-blue-11'
              : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
          "
          :title="preset.note"
          @click="togglePreset(preset.key)"
        >
          {{ preset.label }}
        </button>
      </div>
      <textarea
        v-model="handoverNote"
        rows="2"
        autofocus
        placeholder="(Optional) Anything else — e.g. say sorry to customer, reassure them, price comparison…"
        class="w-full resize-y rounded-lg border border-n-weak bg-n-alpha-1 p-2 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-10 focus:border-n-brand"
        @keydown.esc="closeHandoverPopup"
      />
      <div class="flex items-center justify-end gap-1.5">
        <NextButton ghost slate sm label="Cancel" @click="closeHandoverPopup" />
        <NextButton
          sm
          :disabled="fireBusy"
          label="Hand Over"
          icon="i-lucide-zap"
          @click="fireAssist"
        />
      </div>
    </div>
    <!-- Single morphing badge: countdown during debounce, phase label while
         AI is working. Hides entirely once done/escalated/error/idle so the
         CS toolbar isn't cluttered with stale status. -->
    <div
      v-if="showBadge"
      class="flex items-center gap-1.5 h-7 px-2 rounded-md text-xs font-medium tabular-nums transition-colors"
      :class="badgeClass"
      :title="badgeTitle"
    >
      <span v-if="showCountdown" class="i-lucide-timer text-n-violet-9" />
      <span v-else-if="showError" class="i-lucide-alert-triangle" />
      <span v-else class="i-lucide-loader-2 animate-spin" />
      <span>{{ badgeText }}</span>
    </div>

    <!-- Right action shares one slot: Cancel the whole time the AI is engaged
         (debounce countdown + processing), else Assist Now (idle/done = run). -->
    <NextButton
      v-if="isCancellable"
      ghost
      sm
      class="text-n-ruby-9 hover:enabled:!bg-n-ruby-3 !h-7"
      label="Cancel"
      icon="i-lucide-x"
      :title="cancelTooltip"
      @click="cancelAi"
    />
    <NextButton
      v-else
      ghost
      sm
      :disabled="fireBusy"
      class="text-n-violet-9 hover:enabled:!bg-n-violet-3 !h-7"
      :label="fireBusy ? 'Firing…' : assistLabel"
      icon="i-lucide-zap"
      :title="assistTooltip"
      @click="requestAssist"
    />
  </div>
</template>
